/**
 * Impact indicators — slides 45 and 47 of the finalised pack.
 *
 * Five things this panel refuses to blur:
 *
 *  1. THE LABELS ARE THE LABELS. Exceptional / Full Performance / Not yet full /
 *     Weak Performance, and too soon to rate. "Full contribution" is not a rating
 *     on this scale and must never appear.
 *  2. FULL+ IS OVER THOSE RATED. Too-soon-to-rate people carry an indicator but no
 *     rating, so they sit in the everyone-calibrated spread and OUTSIDE every
 *     percentage.
 *  3. EMPLOYEES LEAD. The tiles, the main spread and the first breakdown are
 *     eligible employees — the same people as the adoption panel. Everyone
 *     calibrated (a few independent contractors and people who have since left
 *     included) sits beside them, never added to them. A copy stored before that
 *     split existed keeps the everyone-calibrated tiles it always had.
 *  4. NULL IS WITHHELD, ABSENT IS NOT AVAILABLE. Identity withholds counts in pairs
 *     so none can be worked out from the others. A null renders as a dash with the
 *     rule attached; a key the response does not carry reads "not available for this
 *     date"; a percentage left out over nobody rated reads "none rated". Nothing
 *     withheld is summed, subtracted, rescaled or drawn — segments are a share of the
 *     PUBLISHED total, and a spread whose total is withheld is shown as counts only.
 *  5. THE KPA CUT IS NOT A SPLIT OF THE WORKFORCE. Function and campus count PEOPLE,
 *     once each; KPA counts RATINGS, so those rows sum past the headcount by design.
 *
 * Date: a review-cycle measure, dated by panelDateLabel. It follows neither the basis
 * toggle nor the period picker. The trend's last point is always the reported cycle,
 * and slide 47's spread by cycle never draws it: its current bar is eligible
 * employees, while the last trend point counts everyone calibrated. Rows keep the
 * served order (the pack's); sorting by a count would place a withheld row by size.
 */
import React, { useMemo, useState, type ReactNode } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Cell,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import { cn } from '@/lib/utils';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  AXIS,
  GRID,
  INK,
  Def,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type IndicatorCell,
  type IndicatorHistoryPoint,
  type IndicatorKey,
  type IndicatorsExtra,
  type RatingKey,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. This measure is deliberately
   *  basis-independent — the same figures are served on every basis — which is
   *  exactly what the window line tells the reader. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

type Dist = Partial<Record<IndicatorKey, number | null>>;

// ---------------------------------------------------------------------------
// The scale, in reading order: best first, unratable last.
// ---------------------------------------------------------------------------

const RATING_ORDER: RatingKey[] = ['EXCEPTIONAL', 'FULL', 'NOT_YET_FULL', 'WEAK'];
const INDICATOR_ORDER: IndicatorKey[] = [...RATING_ORDER, 'TSTR'];

const RATING_LABEL: Record<IndicatorKey, string> = {
  EXCEPTIONAL: 'Exceptional',
  FULL: 'Full Performance',
  NOT_YET_FULL: 'Not yet full',
  WEAK: 'Weak Performance',
  TSTR: 'Too soon to rate',
};

const RATING_COLOR: Record<IndicatorKey, string> = {
  EXCEPTIONAL: '#0B7F72',
  FULL: INK,
  NOT_YET_FULL: '#CFAD80',
  WEAK: '#A8443A',
  TSTR: '#B9C4D6',
};

const WITHHELD_FILL = '#E4E9F1';
const TRACK_FILL = '#F1F4F8';
const BAR_CONTEXT = '#DCE3ED';
const BAR_CURRENT = '#B9C4D6';

const FP_SERIES = 'Full Performance or better';
const RATED_SERIES = 'Rated';

const WITHHELD_TIP =
  'Withheld to protect small groups: fewer than five people. It is not zero, and those people are '
  + 'still inside the totals. Counts are withheld in pairs, so a withheld figure cannot be worked out '
  + 'from the figures beside it.';

/** What each trend point counts, in the board's words. */
const POPULATION_LABEL: Record<string, string> = {
  rated_fo_discussions: 'review records rated in Dynamics F&O',
  all_calibrated: 'everyone calibrated on the platform',
};

/** Rows for a group with no value, in plain words. Every other row reads as served. */
const ROW_LABEL: Record<string, string> = {
  '(no campus)': 'No campus recorded',
  '(unmapped)': 'Function not yet mapped',
};
const rowLabel = (name: string) => ROW_LABEL[name] ?? name;

// ---------------------------------------------------------------------------
// WITHHELD, NOT AVAILABLE, NONE RATED — three different answers, never one dash
// ---------------------------------------------------------------------------

const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    <span aria-hidden="true">—</span>
    <span className="sr-only">withheld to protect small groups</span>
    <Def def={def} k="suppression" text={WITHHELD_TIP} />
  </span>
);

const NotAvailable: React.FC = () => (
  <span className="text-[11px] font-normal text-gray-400" title="Not available for this date">
    <span aria-hidden="true">not available</span>
    <span className="sr-only">not available for this date</span>
  </span>
);

/** A count: null is withheld; a key the response does not carry is not available. */
const Count: React.FC<{ v: number | null | undefined; def: DefLookup }> = ({ v, def }) =>
  v === null ? <Withheld def={def} /> : v === undefined ? <NotAvailable /> : <>{fmtNum(v)}</>;

/** Full Performance or above. null: withheld (also while the rated count is shown).
 *  Left out: "none rated" over nobody rated, otherwise not available for this date. */
const Share: React.FC<{ pct: number | null | undefined; rated: number | null | undefined; def: DefLookup }> = ({
  pct,
  rated,
  def,
}) => {
  if (pct === null) return <Withheld def={def} />;
  if (typeof pct === 'number') return <>{fmtPct(pct)}</>;
  return rated === 0 ? <span className="text-gray-400">none rated</span> : <NotAvailable />;
};

/** Published segments only, each a share of the published total. No total, no bar. */
const StackBar: React.FC<{ dist?: Dist; keys: IndicatorKey[]; total: number | null | undefined; height: number }> = ({
  dist,
  keys,
  total,
  height,
}) => {
  const scale = typeof total === 'number' && total > 0 ? total : null;
  return (
    <div className="flex w-full overflow-hidden rounded-sm" style={{ height, background: TRACK_FILL }}>
      {scale !== null && dist
        ? keys.map((k) => {
            const v = dist[k];
            return typeof v === 'number' && v > 0 ? (
              <span
                key={k}
                className="block h-full"
                style={{ width: `${(v / scale) * 100}%`, background: RATING_COLOR[k] }}
                title={`${RATING_LABEL[k]}: ${fmtNum(v)}`}
              />
            ) : null;
          })
        : null}
    </div>
  );
};

/** A spread with its legend. A withheld segment is left out of the bar and named as
 *  withheld in the legend; its share is never shown. */
const Spread: React.FC<{
  dist?: Dist;
  keys: IndicatorKey[];
  total: number | null | undefined;
  def: DefLookup;
  height?: number;
}> = ({ dist, keys, total, def, height = 22 }) => {
  if (!dist) {
    return (
      <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
        The rating spread is not available for this date.
      </div>
    );
  }
  const scale = typeof total === 'number' && total > 0 ? total : null;
  return (
    <>
      {scale !== null ? (
        <StackBar dist={dist} keys={keys} total={scale} height={height} />
      ) : (
        <div className="rounded border border-gray-100 bg-gray-50 px-2.5 py-1.5 text-[11px] leading-snug text-gray-500">
          {total === null
            ? 'The total is withheld to protect small groups, so this spread is shown as counts only.'
            : total === 0
              ? 'Nobody was rated.'
              : 'The total is not available for this date, so this spread is shown as counts only.'}
        </div>
      )}
      <div
        className="mt-2 grid gap-x-4 gap-y-1"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}
      >
        {keys.map((k) => {
          const v = dist[k];
          return (
            <div key={k} className="flex items-center gap-2 text-[12px]">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ background: typeof v === 'number' ? RATING_COLOR[k] : WITHHELD_FILL }}
              />
              <span className="truncate text-gray-700">{RATING_LABEL[k]}</span>
              <span className="ml-auto shrink-0 font-semibold tabular-nums text-heritage-blue">
                <Count v={v} def={def} />
              </span>
              <span className="w-[50px] shrink-0 text-right tabular-nums text-gray-500">
                {typeof v === 'number' && scale !== null ? fmtPct((v / scale) * 100) : ''}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};

const SectionLabel: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// THE CUT SELECTOR
//
// The same measure, split four ways. Eligible employees by function leads, as the
// tiles do. Function and campus count PEOPLE in post (a person sits in exactly one
// function and at one campus); the KPA cut counts RATINGS — everyone is rated
// against every KPA they carry, so one person appears in several rows. The unit is
// carried on the cut itself, drives the column headings, and is stated above the
// table whenever a non-person cut is selected.
//
// The selector sits on the breakdown section, NOT in the panel header: it switches
// this table only.
// ---------------------------------------------------------------------------

type CutId = 'eligible_function' | 'function' | 'campus' | 'kpa';

interface CutSpec {
  id: CutId;
  /** Chip label. */
  label: string;
  /** Section heading. */
  title: string;
  /** First column heading. */
  heading: string;
  /** Heading for the everyone-with-an-indicator column; the eligible cut has none. */
  countHeading?: string;
  defKey: string;
  unitNote?: (ind: IndicatorsExtra | null) => string;
  read: (ind: IndicatorsExtra | null) => Record<string, IndicatorCell> | undefined;
}

const CUTS: CutSpec[] = [
  {
    id: 'eligible_function',
    label: 'Function, eligible employees',
    title: 'Eligible employees rated, by function',
    heading: 'Function',
    defKey: 'indicators_eligible',
    read: (ind) => ind?.eligible_employees_by_function,
  },
  {
    id: 'function',
    label: 'Function, everyone in post',
    title: 'Calibrated people in post, by function',
    heading: 'Function',
    countHeading: 'With an indicator',
    defKey: 'indicators_by_function',
    read: (ind) => ind?.by_function,
  },
  {
    id: 'campus',
    label: 'Campus',
    title: 'Calibrated people in post, by campus',
    heading: 'Campus',
    countHeading: 'With an indicator',
    defKey: 'indicators_by_campus',
    read: (ind) => ind?.by_campus,
  },
  {
    id: 'kpa',
    label: 'KPA',
    title: 'Ratings by KPA',
    heading: 'KPA',
    countHeading: 'Ratings',
    defKey: 'indicators_by_kpa',
    unitNote: (ind) => {
      const rows = ind?.kpa_rows;
      const tally =
        rows && typeof rows.rows === 'number' && typeof rows.people === 'number'
          ? ` ${fmtNum(rows.rows)} KPA ratings were recorded for ${fmtNum(rows.people)} people.`
          : '';
      return (
        'These are RATINGS PER KPA, not people. Everyone is rated against every KPA they carry, so '
        + 'one person appears in several rows and the rows deliberately sum past the headcount — the '
        + `four pillars are not a split of the workforce.${tally}`
      );
    },
    read: (ind) => ind?.by_kpa,
  },
];

/** Panel-local definitions. The server stays the source of truth: whatever
 *  /metric-definitions publishes for a key wins; these only fill the gap. */
const OWN_DEFS: Record<string, ReactNode> = {
  indicators: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Impact indicators</p>
      <p>
        The calibrated rating each person carries out of the review cycle, on the four-point scale.
        Full Performance or above is Exceptional plus Full Performance, as a share of those rated —
        too soon to rate is recorded but is not a rating, so it sits outside every percentage.
      </p>
      <p className="text-[11px] text-gray-400">
        Source: review cycles on the Impact &amp; Growth Platform; earlier cycles from Dynamics F&amp;O
      </p>
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>A cycle measure — it does not follow the period picker or the basis toggle.</li>
      </ul>
    </div>
  ),
  indicators_eligible: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Eligible employees&apos; ratings</p>
      <p>
        The rating spread for eligible employees — the same people as the adoption panel — counting
        only the four ratings.
      </p>
    </div>
  ),
  indicators_all_calibrated: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Everyone calibrated</p>
      <p>
        Every calibrated rating in the cycle, whoever holds it now: employees, a small number of
        independent contractors, and people who have since left. Includes too soon to rate.
      </p>
    </div>
  ),
  indicators_contractors: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Independent contractors calibrated</p>
      <p>
        Independent contractors in post who carry a calibrated indicator from the cycle. Shown beside
        the employees, never added to them.
      </p>
    </div>
  ),
  indicators_n: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">People with a calibrated indicator</p>
      <p>
        Everyone who came out of the cycle carrying an indicator, including those too soon to rate.
      </p>
    </div>
  ),
  indicators_rated: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Rated</p>
      <p>
        People who received one of the four ratings. Too soon to rate is an indicator but not a
        rating, so those people are excluded here.
      </p>
    </div>
  ),
  indicators_full_plus: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Full Performance or above</p>
      <p>Exceptional plus Full Performance, as a percentage of those rated.</p>
    </div>
  ),
  indicators_exceptional: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Exceptional</p>
      <p>The top indicator on the scale, after calibration.</p>
    </div>
  ),
  indicators_history: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Earlier cycles</p>
      <p>
        Cycles to year-end 2025 are the final rating recorded in Dynamics F&amp;O. Cycles run on the
        platform count everyone calibrated in that cycle, on the same four-point scale. Each cycle
        has its own population, so the number rated is the context the percentage is read against.
      </p>
    </div>
  ),
  indicators_by_function: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">By function</p>
      <p>
        Calibrated people in post, split by Role Master function. Where a group is small, some
        counts and shares are withheld together so that none can be worked out from the others.
      </p>
    </div>
  ),
  indicators_by_campus: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">By campus</p>
      <p>
        Calibrated people in post, split by the campus their position sits at. Where a group is
        small, some counts and shares are withheld together so that none can be worked out from the
        others.
      </p>
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>People, counted once each — a person sits at one campus.</li>
      </ul>
    </div>
  ),
  indicators_by_kpa: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">By key performance area</p>
      <p>
        Ratings given against each of the four key performance areas. This cut counts RATINGS, not
        people: one person is rated against every KPA they carry, so they appear in several rows.
      </p>
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>
          The rows sum past the headcount by design. The pillars are not a split of the workforce.
        </li>
      </ul>
    </div>
  ),
  suppression: <p>{WITHHELD_TIP}</p>,
};

export const IndicatorsPanel: React.FC<Props> = ({ panels, loading, def }) => {
  const ind = readExtras(panels, 'indicators');
  const overall = ind?.overall;
  const ee = ind?.eligible_employees;
  const population = ind?.population;
  const cycle = ind?.cycle;
  const history = useMemo(
    () => (ind?.history ?? []).filter((p): p is IndicatorHistoryPoint => !!p && typeof p === 'object'),
    [ind],
  );
  const current = history.length ? history[history.length - 1] : null;
  const previous = history.length > 1 ? history[history.length - 2] : null;

  const defs = useMemo<DefLookup>(() => (k: string) => def(k) ?? OWN_DEFS[k], [def]);

  // Recharts draws a null as a gap. Whether a missing percentage was withheld or
  // not carried is kept beside it for the tooltip.
  const trend = useMemo(
    () =>
      history.map((pt) => ({
        label: pt.label,
        rated: pt.rated,
        fp_pct: typeof pt.fp_pct === 'number' ? pt.fp_pct : null,
        fpMissing: pt.fp_pct === undefined,
        population: pt.population,
      })),
    [history],
  );
  type TrendDatum = (typeof trend)[number];

  // A change is stated only between two points that count the same thing: a
  // Dynamics F&O year-end against a platform mid-year is not a movement.
  const delta =
    current
    && previous
    && typeof current.fp_pct === 'number'
    && typeof previous.fp_pct === 'number'
    && current.source === previous.source
    && current.population === previous.population
      ? current.fp_pct - previous.fp_pct
      : null;

  // --- slide 47: the spread by cycle ----------------------------------------
  // Every trend point EXCEPT the last, then eligible employees in the current cycle.
  // The last trend point counts everyone calibrated; slide 47's current bar does not.
  const spreadRows = useMemo(() => {
    if (!ee?.dist || !history.some((p) => p.source === 'fo' && p.dist)) return [];
    const earlier = history.slice(0, -1).map((pt, i) => ({
      key: `${pt.label}-${i}`,
      label: pt.source === 'platform' ? pt.label : (pt.name ?? pt.label),
      sub: pt.source === 'platform' ? 'everyone calibrated' : undefined,
      dist: pt.dist as Dist | undefined,
      rated: pt.rated,
      pct: pt.fp_pct,
    }));
    const cycleLabel = cycle
      ? `${cycle.review_type === 'YEAREND' ? 'Year-end' : 'Mid-year'} ${cycle.review_year}`
      : 'Current cycle';
    return [
      ...earlier,
      {
        key: 'current',
        label: cycleLabel,
        sub: 'eligible employees',
        dist: ee.dist as Dist | undefined,
        rated: ee.rated,
        pct: ee.full_plus_pct,
      },
    ];
  }, [ee, history, cycle]);

  // --- breakdown ------------------------------------------------------------
  // Only cuts this date carries are offered; the selection falls back to the
  // first available one rather than rendering an empty table.
  const availableCuts = useMemo(
    () => CUTS.filter((c) => Object.keys(c.read(ind) ?? {}).length > 0),
    [ind],
  );
  const [cutId, setCutId] = useState<CutId | null>(null);
  const cut = availableCuts.find((c) => c.id === cutId) ?? availableCuts[0] ?? null;

  const cutRows = useMemo(
    () =>
      Object.entries(cut?.read(ind) ?? {}).filter(
        (e): e is [string, IndicatorCell] => !!e[1] && typeof e[1] === 'object',
      ),
    [ind, cut],
  );
  // null never reaches Math.max: a withheld share has no bar rather than a zero one.
  const maxCutPct = Math.max(
    0,
    ...cutRows.map(([, v]) => v.full_plus_pct).filter((v): v is number => typeof v === 'number'),
  );
  const rowsWithheld = cutRows.filter(([, v]) => v.suppressed === true || v.rated === null).length;
  const sharesWithheld = cutRows.filter(
    ([, v]) => typeof v.rated === 'number' && v.suppressed !== true && v.full_plus_pct === null,
  ).length;

  const dateLabel = panelDateLabel(panels, 'indicators');
  const windowNote = ee
    ? `${dateLabel}. Calibrated impact indicators for ${cycle?.name ?? 'the current review cycle'}. `
      + 'The main figures are eligible employees, the same people as the adoption panel; everyone '
      + 'calibrated includes a small number of independent contractors and people who have since left. '
      + 'Earlier cycles are the final rating recorded in Dynamics F&O, then everyone calibrated in each '
      + 'platform cycle. Where a group is small, some counts and shares are withheld together. Follows '
      + 'neither the period picker nor the basis toggle.'
    : `${dateLabel}. Calibrated indicators for ${cycle?.name ?? 'the review cycle'}, counting everyone `
      + 'calibrated. Follows neither the period picker nor the basis toggle.';

  return (
    <PanelShell
      title="Impact indicators"
      defKey="indicators"
      def={defs}
      window={windowNote}
      footnote={
        <>
          The scale is Exceptional, Full Performance, Not yet full and Weak Performance. “Too soon to
          rate” is recorded but is not a rating, so it sits outside every percentage — Full
          Performance or above is Exceptional plus Full Performance over those rated. The breakdown
          can be cut by function, campus or KPA; the KPA cut counts RATINGS, not people, so those rows
          sum past the headcount. A dash marked withheld is a group of fewer than five, withheld to
          protect small groups — it is not zero. “Not available” means this date carries no such
          figure.
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={320} />
      ) : !panels ? (
        <PanelUnavailable what="Impact indicators" />
      ) : !ind || (!overall && !ee) ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          Impact indicators are not available for this date.
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── tiles: eligible employees first ────────────────────────── */}
          {ee ? (
            <TileRow min="150px">
              <MetricTile
                label="Eligible employees rated"
                value={<Count v={ee.rated} def={defs} />}
                sub={
                  typeof ee.people === 'number'
                    ? `of ${fmtNum(ee.people)} eligible employees`
                    : 'eligible employees, four ratings'
                }
                defKey="indicators_eligible"
                def={defs}
                size="sm"
              />
              <MetricTile
                label="Full Performance or above"
                value={<Share pct={ee.full_plus_pct} rated={ee.rated} def={defs} />}
                sub="eligible employees, over those rated"
                defKey="indicators_full_plus"
                def={defs}
                size="sm"
              />
              <MetricTile
                label="Everyone calibrated"
                value={<Count v={overall?.n} def={defs} />}
                sub={
                  typeof population?.tstr === 'number'
                    ? `including ${fmtNum(population.tstr)} too soon to rate`
                    : undefined
                }
                defKey="indicators_all_calibrated"
                def={defs}
                size="sm"
              />
              {population ? (
                <MetricTile
                  label="Independent contractors calibrated"
                  value={<Count v={population.independent_contractors} def={defs} />}
                  sub="beside employees, never added to them"
                  defKey="indicators_contractors"
                  def={defs}
                  size="sm"
                />
              ) : null}
            </TileRow>
          ) : overall ? (
            <TileRow min="150px">
              <MetricTile
                label="With an indicator"
                value={<Count v={overall.n} def={defs} />}
                sub="calibrated, incl. too soon to rate"
                defKey="indicators_n"
                def={defs}
                size="sm"
              />
              <MetricTile
                label="Rated"
                value={<Count v={overall.rated} def={defs} />}
                sub="received one of the four ratings"
                defKey="indicators_rated"
                def={defs}
                size="sm"
              />
              <MetricTile
                label="Full Performance or better"
                value={<Share pct={overall.full_plus_pct} rated={overall.rated} def={defs} />}
                sub={
                  <>
                    <Count v={overall.full_plus} def={defs} /> of <Count v={overall.rated} def={defs} />{' '}
                    rated
                  </>
                }
                defKey="indicators_full_plus"
                def={defs}
                size="sm"
              />
              <MetricTile
                label="Exceptional"
                value={<Count v={overall.exceptional} def={defs} />}
                sub="top of the scale"
                defKey="indicators_exceptional"
                def={defs}
                size="sm"
              />
            </TileRow>
          ) : null}

          {/* ── rating spread ──────────────────────────────────────────── */}
          <div>
            <SectionLabel>
              {ee ? 'Rating spread, eligible employees' : 'Rating distribution'}
              <Def def={defs} k={ee ? 'indicators_eligible' : 'indicators'} />
            </SectionLabel>
            {ee ? (
              <Spread dist={ee.dist} keys={RATING_ORDER} total={ee.rated} def={defs} />
            ) : (
              <Spread dist={overall?.dist} keys={INDICATOR_ORDER} total={overall?.n} def={defs} />
            )}
            {ee && overall ? (
              <div className="mt-4">
                <SectionLabel>
                  Everyone calibrated, including too soon to rate
                  <Def def={defs} k="indicators_all_calibrated" />
                </SectionLabel>
                <Spread dist={overall.dist} keys={INDICATOR_ORDER} total={overall.n} def={defs} height={12} />
              </div>
            ) : null}
          </div>

          {/* ── trend by cycle ─────────────────────────────────────────── */}
          <div>
            <SectionLabel>
              Full Performance or better, by cycle
              <Def def={defs} k="indicators_history" />
            </SectionLabel>
            {trend.length ? (
              <>
                <div style={{ width: '100%', height: 236 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={trend} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: AXIS }}
                      />
                      <YAxis
                        yAxisId="rated"
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={46}
                        tickFormatter={(v: number) => fmtNum(v)}
                      />
                      <YAxis
                        yAxisId="pct"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={42}
                        tickFormatter={(v: number) => `${v}%`}
                      />
                      <RechartsTooltip
                        labelStyle={{ fontWeight: 600 }}
                        contentStyle={{ fontSize: 12 }}
                        labelFormatter={(label: string, payload: Array<{ payload?: TrendDatum }>) => {
                          const who = payload?.[0]?.payload?.population;
                          return who && POPULATION_LABEL[who] ? `${label} · ${POPULATION_LABEL[who]}` : label;
                        }}
                        formatter={(v: number, name: string, item: { payload?: TrendDatum }) => {
                          if (typeof v === 'number') return [name === FP_SERIES ? fmtPct(v) : fmtNum(v), name];
                          const missing = name === FP_SERIES && item?.payload?.fpMissing;
                          return [
                            missing ? 'not available for this date' : 'withheld to protect small groups',
                            name,
                          ];
                        }}
                      />
                      <Bar
                        yAxisId="rated"
                        dataKey="rated"
                        name={RATED_SERIES}
                        barSize={28}
                        isAnimationActive={false}
                      >
                        {trend.map((h, i) => (
                          <Cell
                            key={`${h.label}-${i}`}
                            fill={i === trend.length - 1 ? BAR_CURRENT : BAR_CONTEXT}
                          />
                        ))}
                      </Bar>
                      {current ? (
                        <ReferenceLine
                          yAxisId="pct"
                          x={current.label}
                          stroke={INK}
                          strokeDasharray="3 4"
                          strokeOpacity={0.45}
                          label={{
                            value: 'current cycle',
                            position: 'top',
                            fontSize: 9,
                            fill: '#94a3b8',
                          }}
                        />
                      ) : null}
                      <Line
                        yAxisId="pct"
                        type="monotone"
                        dataKey="fp_pct"
                        name={FP_SERIES}
                        stroke={INK}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: INK, strokeWidth: 0 }}
                        isAnimationActive={false}
                      />
                      {current && typeof current.fp_pct === 'number' ? (
                        <ReferenceDot
                          yAxisId="pct"
                          x={current.label}
                          y={current.fp_pct}
                          r={5.5}
                          fill={ACCENT}
                          stroke="#ffffff"
                          strokeWidth={2}
                          isFront
                        />
                      ) : null}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                {current ? (
                  <div className="mt-1 text-[12.5px] tabular-nums text-gray-600">
                    <b className="text-heritage-blue">{current.label}</b>:{' '}
                    <Share pct={current.fp_pct} rated={current.rated} def={defs} /> Full Performance or
                    better across <Count v={current.rated} def={defs} /> rated
                    {current.population && POPULATION_LABEL[current.population]
                      ? ` (${POPULATION_LABEL[current.population]})`
                      : ''}
                    {ee ? (
                      <>
                        ; eligible employees{' '}
                        <Share pct={ee.full_plus_pct} rated={ee.rated} def={defs} />
                      </>
                    ) : null}
                    {delta !== null && previous ? (
                      <>
                        {' '}
                        — {delta >= 0 ? 'up' : 'down'} {Math.abs(delta).toFixed(1)} percentage points on{' '}
                        {previous.label}, which rated <Count v={previous.rated} def={defs} />
                      </>
                    ) : null}
                    . Bars are the number rated; the line is the percentage.
                  </div>
                ) : null}
              </>
            ) : (
              <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
                Earlier cycles are not available for this date — only the current cycle above.
              </div>
            )}
          </div>

          {/* ── slide 47: rating spread by cycle ───────────────────────── */}
          {spreadRows.length ? (
            <section aria-label="Rating spread by cycle">
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Rating spread by cycle
                  <Def def={defs} k="indicators_history" />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {RATING_ORDER.map((k) => (
                    <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ background: RATING_COLOR[k] }}
                      />
                      {RATING_LABEL[k]}
                    </span>
                  ))}
                </div>
              </div>
              <div
                className="grid items-end gap-2.5 border-b pb-1 text-[11px] font-medium text-gray-500"
                style={{ gridTemplateColumns: 'minmax(0, 150px) 1fr 58px 52px' }}
              >
                <span>Cycle</span>
                <span />
                <span className="text-right">Rated</span>
                <span className="text-right">Full+</span>
              </div>
              <div className="divide-y divide-gray-50">
                {spreadRows.map((row) => {
                  const withheld = RATING_ORDER.filter((k) => row.dist?.[k] === null);
                  return (
                    <div
                      key={row.key}
                      className="grid items-center gap-2.5 py-1 text-[12px]"
                      style={{ gridTemplateColumns: 'minmax(0, 150px) 1fr 58px 52px' }}
                    >
                      <div className="min-w-0 leading-tight">
                        <div className="truncate text-gray-700">{row.label}</div>
                        {row.sub ? <div className="text-[10.5px] text-gray-500">{row.sub}</div> : null}
                      </div>
                      <div className="min-w-0">
                        {row.dist ? (
                          <StackBar dist={row.dist} keys={RATING_ORDER} total={row.rated} height={13} />
                        ) : (
                          <NotAvailable />
                        )}
                        {withheld.length ? (
                          <div className="mt-0.5 text-[10.5px] leading-tight text-gray-500">
                            {withheld.map((k) => RATING_LABEL[k]).join(', ')} withheld to protect small
                            groups
                          </div>
                        ) : null}
                      </div>
                      <div className="text-right tabular-nums text-gray-600">
                        <Count v={row.rated} def={defs} />
                      </div>
                      <div className="text-right font-semibold tabular-nums text-heritage-blue">
                        <Share pct={row.pct} rated={row.rated} def={defs} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-1 text-[11px] leading-snug text-gray-500">
                Each bar is that cycle&apos;s spread over those rated. The last bar is eligible employees
                in the current cycle, the same people as the tiles above; the trend&apos;s current
                point counts everyone calibrated instead, so it is not repeated here.
              </div>
            </section>
          ) : null}

          {/* ── breakdown: eligible by function / function / campus / KPA ─ */}
          {cut && cutRows.length ? (
            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
                <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  {cut.title}
                  <Def def={defs} k={cut.defKey} />
                </div>
                {availableCuts.length > 1 ? (
                  <div
                    className="flex flex-wrap items-center gap-1.5"
                    role="group"
                    aria-label="Breakdown cut"
                  >
                    {availableCuts.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        aria-pressed={cut.id === c.id}
                        onClick={() => setCutId(c.id)}
                        className={cn(
                          'rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                          cut.id === c.id
                            ? 'border-heritage-blue bg-heritage-blue text-white'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                        )}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              {cut.unitNote ? (
                <div className="mb-1.5 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-snug text-amber-800">
                  {cut.unitNote(ind)}
                </div>
              ) : null}

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b text-gray-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">
                        <span className="inline-flex items-center">
                          {cut.heading}
                          <Def def={defs} k={cut.defKey} />
                        </span>
                      </th>
                      <th className="px-2 py-1.5 text-left font-medium" style={{ width: '28%' }}>
                        <span className="sr-only">
                          {cut.id === 'eligible_function'
                            ? 'Rating spread'
                            : 'Full Performance or better, relative'}
                        </span>
                      </th>
                      {cut.countHeading ? (
                        <th className="px-2 py-1.5 text-right font-medium">{cut.countHeading}</th>
                      ) : null}
                      <th className="px-2 py-1.5 text-right font-medium">Rated</th>
                      <th className="px-2 py-1.5 text-right font-medium">
                        <span className="inline-flex items-center justify-end">
                          Full+
                          <Def def={defs} k="indicators_full_plus" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cutRows.map(([name, v]) => {
                      const pct = typeof v.full_plus_pct === 'number' ? v.full_plus_pct : null;
                      return (
                        <tr key={name} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="min-w-[150px] max-w-[280px] px-2 py-1.5 text-gray-700" title={name}>
                            {rowLabel(name)}
                          </td>
                          <td className="px-2 py-1.5">
                            {cut.id === 'eligible_function' ? (
                              <StackBar dist={v.dist} keys={RATING_ORDER} total={v.rated} height={10} />
                            ) : (
                              <div className="relative h-[10px] overflow-hidden rounded-sm bg-gray-100">
                                {pct !== null && pct > 0 && maxCutPct > 0 ? (
                                  <span
                                    className="absolute inset-y-0 left-0 rounded-sm"
                                    style={{
                                      width: `${Math.max(0.8, (pct / maxCutPct) * 100)}%`,
                                      background: ACCENT,
                                    }}
                                  />
                                ) : null}
                              </div>
                            )}
                          </td>
                          {cut.countHeading ? (
                            <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">
                              <Count v={v.n} def={defs} />
                            </td>
                          ) : null}
                          <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                            <Count v={v.rated} def={defs} />
                          </td>
                          <td
                            className={cn(
                              'px-2 py-1.5 text-right tabular-nums',
                              pct === null ? 'text-gray-400' : 'font-medium text-gray-800',
                            )}
                          >
                            <Share pct={v.full_plus_pct} rated={v.rated} def={defs} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-1 text-[11px] leading-snug text-gray-500">
                {cut.id === 'kpa'
                  ? 'One person contributes a rating to every KPA they carry, so these rows are ratings and sum past the number of people above.'
                  : cut.id === 'eligible_function'
                    ? 'Eligible employees rated, each in exactly one function row; too soon to rate is not counted here.'
                    : `Each person appears in exactly one ${cut.heading.toLowerCase()} row, so these rows are a split of the calibrated people in post.`}
                {rowsWithheld > 0 ? (
                  <span className="text-amber-700">
                    {' '}
                    {rowsWithheld} {rowsWithheld === 1 ? 'row is' : 'rows are'} withheld to protect small
                    groups (fewer than five) — not zero.
                  </span>
                ) : null}
                {sharesWithheld > 0 ? (
                  <span className="text-amber-700">
                    {' '}
                    {sharesWithheld} {sharesWithheld === 1 ? 'row shows' : 'rows show'} the number rated
                    but withhold the share, which would otherwise give a group of fewer than five away.
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
